/**
 * LocalStorageService
 * 
 * A centralized service for managing user data in localStorage.
 * This service provides functions for storing, retrieving, and validating user data.
 */

// Constants
const STORAGE_KEYS = {
  USER_DATA: 'userData',
  USER_DATA_TIMESTAMP: 'userDataTimestamp',
  USER_EMAIL: 'userEmail',
  FORCE_AUTH_REFRESH: 'forceAuthRefresh'
};

// Cache validity period (24 hours in milliseconds)
// Since subscription data only changes through the webapp itself, we use a long validity period
const MAX_CACHE_AGE_MS = 24 * 60 * 60 * 1000;

/**
 * Store user data in localStorage
 * @param {Object} userData - The user data to store
 * @param {boolean} debug - Whether to log debug information
 */
export const storeUserData = (userData, debug = false) => {
  if (!userData) {
    console.error('LocalStorageService: Cannot store null or undefined user data');
    return;
  }

  try {
    localStorage.setItem(STORAGE_KEYS.USER_DATA, JSON.stringify(userData));
    localStorage.setItem(STORAGE_KEYS.USER_DATA_TIMESTAMP, Date.now().toString());
    
    if (debug) {
      console.log('LocalStorageService: User data stored with subscription type:', userData.stripeSubscriptionType);
      
      // Verify the data was stored correctly
      const storedData = localStorage.getItem(STORAGE_KEYS.USER_DATA);
      try {
        const parsedData = JSON.parse(storedData);
        console.log('LocalStorageService: Verified stored subscription type:', parsedData.stripeSubscriptionType);
      } catch (e) {
        console.error('LocalStorageService: Error verifying stored data:', e);
      }
    }
  } catch (error) {
    console.error('LocalStorageService: Error storing user data:', error);
  }
};

/**
 * Store user email in localStorage
 * @param {string} email - The user's email
 */
export const storeUserEmail = (email) => {
  if (!email) {
    console.error('LocalStorageService: Cannot store null or undefined email');
    return;
  }

  try {
    localStorage.setItem(STORAGE_KEYS.USER_EMAIL, email);
  } catch (error) {
    console.error('LocalStorageService: Error storing user email:', error);
  }
};

/**
 * Get user data from localStorage
 * @param {boolean} validateTimestamp - Whether to validate the timestamp
 * @param {boolean} debug - Whether to log debug information
 * @returns {Object|null} The user data or null if not found or invalid
 */
export const getUserData = (validateTimestamp = true, debug = false) => {
  try {
    const storedUserData = localStorage.getItem(STORAGE_KEYS.USER_DATA);
    const storedTimestamp = localStorage.getItem(STORAGE_KEYS.USER_DATA_TIMESTAMP);
    
    if (debug) {
      console.log('LocalStorageService: Stored user data:', storedUserData ? 'exists' : 'not found');
      if (storedUserData) {
        try {
          const parsedData = JSON.parse(storedUserData);
          console.log('LocalStorageService: Parsed stored subscription type:', parsedData.stripeSubscriptionType);
        } catch (e) {
          console.error('LocalStorageService: Error parsing stored user data:', e);
        }
      }
    }
    
    // Check if we have valid stored data
    const isStoredDataValid = storedUserData && 
      (!validateTimestamp || (storedTimestamp && (Date.now() - parseInt(storedTimestamp)) < MAX_CACHE_AGE_MS));
    
    if (debug) {
      console.log('LocalStorageService: Stored data valid?', isStoredDataValid);
      if (storedTimestamp) {
        console.log('LocalStorageService: Data age (ms):', Date.now() - parseInt(storedTimestamp));
      }
    }
    
    if (isStoredDataValid) {
      return JSON.parse(storedUserData);
    }
    
    return null;
  } catch (error) {
    console.error('LocalStorageService: Error getting user data:', error);
    return null;
  }
};

/**
 * Get user email from localStorage
 * @returns {string|null} The user's email or null if not found
 */
export const getUserEmail = () => {
  try {
    return localStorage.getItem(STORAGE_KEYS.USER_EMAIL);
  } catch (error) {
    console.error('LocalStorageService: Error getting user email:', error);
    return null;
  }
};

/**
 * Clear user data from localStorage
 */
export const clearUserData = () => {
  try {
    localStorage.removeItem(STORAGE_KEYS.USER_DATA);
    localStorage.removeItem(STORAGE_KEYS.USER_DATA_TIMESTAMP);
  } catch (error) {
    console.error('LocalStorageService: Error clearing user data:', error);
  }
};

/**
 * Set the force auth refresh flag
 */
export const setForceAuthRefresh = () => {
  try {
    localStorage.setItem(STORAGE_KEYS.FORCE_AUTH_REFRESH, 'true');
  } catch (error) {
    console.error('LocalStorageService: Error setting force auth refresh flag:', error);
  }
};

/**
 * Clear the force auth refresh flag
 */
export const clearForceAuthRefresh = () => {
  try {
    localStorage.removeItem(STORAGE_KEYS.FORCE_AUTH_REFRESH);
  } catch (error) {
    console.error('LocalStorageService: Error clearing force auth refresh flag:', error);
  }
};

/**
 * Check if force auth refresh flag is set
 * @returns {boolean} Whether the force auth refresh flag is set
 */
export const isForceAuthRefreshSet = () => {
  try {
    return localStorage.getItem(STORAGE_KEYS.FORCE_AUTH_REFRESH) === 'true';
  } catch (error) {
    console.error('LocalStorageService: Error checking force auth refresh flag:', error);
    return false;
  }
};

// Export all constants and functions
const localStorageService = {
  STORAGE_KEYS,
  MAX_CACHE_AGE_MS,
  storeUserData,
  storeUserEmail,
  getUserData,
  getUserEmail,
  clearUserData,
  setForceAuthRefresh,
  clearForceAuthRefresh,
  isForceAuthRefreshSet
};

export default localStorageService;
